import { ref, computed } from 'vue'

import type { KanbanComment } from '@entities/kanban-comment'
import type { AppTextareaEvent } from '@shared/ui/fields'

import { useCommonStore } from '@shared/model'

interface KanbanCommentsOptions {
  saveApi?: (comment: string) => Promise<KanbanComment | null>
  updateApi: (commentId: number, comment: string) => Promise<KanbanComment | null>
}

interface KanbanCommentsReturn {
  refShowForm: Ref<boolean>
  refComment: Ref<string>
  refEditingCommentId: Ref<number | null>
  refIsEditing: ComputedRef<boolean>
  refLabel: ComputedRef<string>
  refIsDisabled: ComputedRef<boolean>
  setShowForm: () => void
  setCloseForm: () => void
  setComment: (event: AppTextareaEvent) => void
  setEditComment: (comment: KanbanComment) => void
  setSubmitComment: () => Promise<void>
}

export const useKanbanComments = (options: KanbanCommentsOptions): KanbanCommentsReturn => {
  const commonStore = useCommonStore()

  const refShowForm = ref<boolean>(false)
  const refComment = ref<string>('')
  const refEditingCommentId = ref<number | null>(null)

  const refIsEditing = computed<boolean>(() => refEditingCommentId.value !== null)
  const refLabel = computed<string>(() => refIsEditing.value ? 'Edit comment' : 'Add comment')
  const refIsDisabled = computed<boolean>(() => !refComment.value.trim() || commonStore.isLoading)

  const setShowForm = (): void => {
    refShowForm.value = true
  }

  const setCloseForm = (): void => {
    refShowForm.value = false
    refComment.value = ''
    refEditingCommentId.value = null
  }

  const setComment = (event: AppTextareaEvent): void => {
    refComment.value = event.value
  }

  const setEditComment = (comment: KanbanComment): void => {
    refEditingCommentId.value = comment.id
    refComment.value = comment.comment
    refShowForm.value = true
  }

  const setSubmitComment = async (): Promise<void> => {
    if (refIsDisabled.value) return

    let result: KanbanComment | null = null
    if (refIsEditing.value) {
      result = await options.updateApi(refEditingCommentId.value!, refComment.value)
    } else if (options.saveApi) {
      result = await options.saveApi(refComment.value)
    }

    if (result) setCloseForm()
  }

  return {
    refShowForm,
    refComment,
    refEditingCommentId,
    refIsEditing,
    refLabel,
    refIsDisabled,
    setShowForm,
    setCloseForm,
    setComment,
    setEditComment,
    setSubmitComment,
  }
}